import React from 'react';
import { TagChip } from '@/shared/components/ui';
import type { CustomerResponse } from '@troia-v3/types';
import { Avatar, AvatarImage, AvatarFallback, StatusPill } from '@/shared/components/ui';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';
import { getCustomerTypeBadge } from './customerTypeBadge';

/** Iniciais do nome do cliente (2 letras). Avatar sem foto usa slate (AvatarFallback). */
function getCustomerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
}

/**
 * Card de cliente (view "Cards" + fallback mobile da view "Lista"). Padrão-lista
 * travado (DS 3.x, espelha `UserCard`): card flat/borda SEM checkbox (F34 —
 * seleção em massa só na view de lista/tabela) + menu ••• de ações sempre
 * visível. Avatar sem foto = slate. Preserva badges tipo/status, contato
 * (email/phone) e tags do `CustomerCard` original.
 */
export interface CustomerGridCardProps {
  customer: CustomerResponse;
  onOpen: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const CustomerGridCard: React.FC<CustomerGridCardProps> = ({
  customer,
  onOpen,
  onEdit,
  onDelete,
}) => {
  const typeBadge = getCustomerTypeBadge(customer.type);
  const isActive = customer.status === 'active';

  const actions: CardAction[] = [];
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(customer.id) });
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(customer.id), variant: 'danger' });

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors cursor-pointer"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
        opacity: isActive ? 1 : 0.72,
      }}
      onClick={() => onOpen(customer.id)}
      data-testid={`customers-card-${customer.id}`}
    >
      {/* Header: avatar + nome + segmento + ações */}
      <div className="flex items-start gap-3">
        <Avatar className="w-10 h-10 flex-shrink-0" isSquircle>
          {customer.picture && <AvatarImage src={customer.picture} alt={customer.name} isSquircle />}
          <AvatarFallback isSquircle>
            <span className="text-white font-semibold text-sm">{getCustomerInitials(customer.name)}</span>
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {customer.name}
          </h3>
          <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {customer.segment || '—'}
          </p>
        </div>

        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1 -mt-1" onClick={(e) => e.stopPropagation()}>
            <CardActionsMenu actions={actions} size="sm" data-testid={`customers-card-actions-${customer.id}`} />
          </div>
        )}
      </div>

      {/* Badges: tipo + status */}
      <div className="mt-3 flex items-center flex-wrap gap-2">
        <span
          className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
        >
          <Icon name={typeBadge.icon} className="w-3 h-3" />
          {typeBadge.label}
        </span>
        <StatusPill
          variant={isActive ? 'success' : 'neutral'}
          label={isActive ? 'Ativo' : 'Inativo'}
          data-testid={`customers-card-status-${customer.id}`}
        />
      </div>

      {/* Contato */}
      {(customer.email || customer.phone) && (
        <div className="mt-3 pt-3 space-y-1" style={{ borderTop: '1px solid var(--theme-card-border)' }}>
          {customer.email && (
            <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              <Icon name="mail" className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{customer.email}</span>
            </div>
          )}
          {customer.phone && (
            <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              <Icon name="phone" className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{customer.phone}</span>
            </div>
          )}
        </div>
      )}

      {/* Tags */}
      {customer.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {customer.tags.slice(0, 4).map((tag) => (
            <TagChip
              key={tag}
              name={tag}
              className="text-xs font-medium"
              style={{ height: 22, padding: '0 8px' }}
            />
          ))}
          {customer.tags.length > 4 && (
            <span className="text-xs self-center" style={{ color: 'var(--theme-muted-text)' }}>
              +{customer.tags.length - 4}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

CustomerGridCard.displayName = 'CustomerGridCard';
